import React, { FC, Fragment, useState, useRef, useCallback } from 'react';
import { Popper, ClickAwayListener, Paper, PopperPlacementType, Fade } from '@mui/material';

type ContentProps = {
  closeDropdown: () => void;
}

export type DropdownProps = {
  content: React.ReactElement | ((props: ContentProps) => React.ReactElement);
  placement?: PopperPlacementType;
  style?: React.CSSProperties;
  children: (props: {
    ref: React.RefObject<HTMLButtonElement>;
    handleToggleDropdownMenu: () => void;
    openDropdownMenu: boolean;
  }) => React.ReactElement;
}

export const Dropdown: FC<DropdownProps> = ({ children, content, placement, style = {} }) => {

  const [openDropdownMenu, setOpenDropdownMenu] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggleDropdownMenu = useCallback(() => {
    setOpenDropdownMenu((prevOpen) => !prevOpen);
  }, []);

  const handleClose = useCallback(() => {
    setOpenDropdownMenu(false);
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    handleClose();
  }, [handleClose]);

  return (
    <Fragment>
      {children({
        ref: anchorRef,
        handleToggleDropdownMenu,
        openDropdownMenu,
      })}
      <Popper
        open={openDropdownMenu}
        anchorEl={anchorRef.current}
        role={undefined}
        placement={placement}
        transition
        style={{ zIndex: 10 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper style={{ ...style }}>
              <ClickAwayListener onClickAway={handleClickOutside}>
                {typeof content === 'function' ? content({ closeDropdown: handleClose }) : content}
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Fragment>
  );
};
